import { useMemo } from "react";
import { FormatNumber } from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import type { Currency } from "@krea/shared/types/common";
import { formatLenderMonthlyInterestRate } from "@krea/shared/utils/formatNumber";

import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type { IBuildingCreditBid } from "@/features/lender-responses/types/lender-response";
import { capitalizeWithSpaces } from "@/utils";

import type { IBidDetailsResponse } from "../../BidDetails";
import { BidDetailsListItem, EmptyField } from "../../bidHelpers";

interface IBuildingCreditBidProps {
  bid: IBuildingCreditBid;
  currency: Currency;
}

export const BuildingCreditBid = ({
  bid,
  currency,
}: IBuildingCreditBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const interestMonthly = bid.comparableMonthlyInterestRate;
  const averageMonthlyCost = bid.averageMonthlyCost;

  // Exactly one of LTV/LTC is ever set — guaranteed server-side (KREA-4400).
  const maxFinancing =
    bid.maxFinancingLtv != null
      ? {
          label: "Max financing (% of property value)",
          value: bid.maxFinancingLtv,
        }
      : bid.maxFinancingLtc != null
        ? {
            label: "Max financing (% of project cost)",
            value: bid.maxFinancingLtc,
          }
        : null;

  const details = useMemo(() => {
    return (
      <div>
        {!!bid.amount && (
          <BidDetailsListItem
            title="Credit limit"
            value={formattedCurrencyValue(bid.amount)}
          />
        )}

        {!!bid.paybackTimeMonths && (
          <BidDetailsListItem
            title="Loan term"
            value={`${bid.paybackTimeMonths} months`}
          />
        )}

        {!!bid.lenderMonthlyInterest && (
          <BidDetailsListItem
            title={
              isInterestRatePerYearActivated
                ? "Interest (per year)"
                : "Interest (per month)"
            }
            value={`${formatLenderMonthlyInterestRate(bid.lenderMonthlyInterest, "TO_YEARLY", isInterestRatePerYearActivated)}% / ${isInterestRatePerYearActivated ? "year" : "30 days"}`}
          />
        )}

        {!!bid.liftFeePercentage && (
          <BidDetailsListItem
            title="Lift fee (in % of drawn amount)"
            value={`${bid.liftFeePercentage}%`}
          />
        )}

        <BidDetailsListItem
          title="Payment type"
          value={capitalizeWithSpaces(bid.paymentType)}
        />

        <BidDetailsListItem
          title="Interest payment frequency"
          value={capitalizeWithSpaces(bid.paymentFrequency)}
        />

        {maxFinancing && (
          <BidDetailsListItem
            title={maxFinancing.label}
            value={`${maxFinancing.value}%`}
          />
        )}

        {!!bid.limitFeePerYearPercent && (
          <BidDetailsListItem
            title="Limit fee (per year)"
            value={`${bid.limitFeePerYearPercent}%`}
          />
        )}

        {!!averageMonthlyCost && (
          <BidDetailsListItem
            title="Interest payment"
            value={formattedCurrencyValue(averageMonthlyCost)}
          />
        )}

        {!!bid.setupFee && (
          <BidDetailsListItem
            title="Setup fee (paid upfront)"
            value={formattedCurrencyValue(bid.setupFee)}
          />
        )}

        {!!bid.administrationFees && (
          <BidDetailsListItem
            title="Administration fees (per month)"
            value={formattedCurrencyValue(bid.administrationFees)}
          />
        )}
      </div>
    );
  }, [
    bid,
    formattedCurrencyValue,
    averageMonthlyCost,
    isInterestRatePerYearActivated,
    maxFinancing,
  ]);

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Credit limit"
            value={
              bid.amount ? formattedCurrencyValue(bid.amount) : <EmptyField />
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Loan term"
            value={
              bid.paybackTimeMonths ? (
                `${bid.paybackTimeMonths} months`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              interestMonthly ? (
                `${FormatNumber(interestMonthly)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title={maxFinancing?.label ?? "Max financing"}
            value={maxFinancing ? `${maxFinancing.value}%` : <EmptyField />}
          />
        </div>
      </div>
    );
  }, [bid, formattedCurrencyValue, interestMonthly, maxFinancing]);

  return {
    summary,
    details,
  };
};
